/**
 * Custom React hook for audio recording
 * 
 * Handles MediaRecorder API, audio level monitoring, and upload integration
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.1: Web Audio Recorder Component - Setup
 * 
 * @example
 * ```tsx
 * const { state, controls } = useAudioRecorder({
 *   quality: 'high',
 *   maxDuration: 3600,
 *   onStop: (data) => console.log('Recording stopped', data)
 * });
 * 
 * <button onClick={controls.startRecording}>Record</button>
 * ```
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { AudioUploadClient, getWebDeviceInfo } from '@/lib/audio-clients/audio-upload-client';
import { getQualitySettings } from '@/lib/audio-clients/audio-types';
import { getIdToken } from '@/lib/native-auth';
import { getAudioAPIConfig } from '@/lib/audio-api-config';
import {
  RecordingState,
  AudioRecorderError,
  RecordingData,
  AudioRecorderConfig,
  AudioRecorderState,
  AudioRecorderControls,
  BrowserSupport,
} from './AudioRecorder.types';
import {
  parseError,
  createErrorDetails,
  AudioRecorderErrorLogger,
  AudioRecorderErrorRecovery,
  AudioRecorderErrorDetails,
} from './AudioRecorderErrors';

/**
 * Detect browser support for MediaRecorder
 */
function detectBrowserSupport(): BrowserSupport {
  const mediaRecorderSupported = 'MediaRecorder' in window;
  
  if (!mediaRecorderSupported) {
    return {
      mediaRecorderSupported: false,
      preferredMimeType: null,
      supportedMimeTypes: [],
      webmSupported: false,
      oggSupported: false,
      mp4Supported: false,
    };
  }

  const mimeTypes = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/wav',
  ];

  const supportedMimeTypes = mimeTypes.filter(type => MediaRecorder.isTypeSupported(type));
  const preferredMimeType = supportedMimeTypes[0] || null;

  return {
    mediaRecorderSupported: true,
    preferredMimeType,
    supportedMimeTypes,
    webmSupported: supportedMimeTypes.some(t => t.includes('webm')),
    oggSupported: supportedMimeTypes.some(t => t.includes('ogg')),
    mp4Supported: supportedMimeTypes.some(t => t.includes('mp4')),
  };
}

/**
 * Extract format from MIME type
 */
function getFormatFromMimeType(mimeType: string): string {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('wav')) return 'wav';
  return 'unknown';
}

/**
 * Custom hook for audio recording
 */
export function useAudioRecorder(config: AudioRecorderConfig = {}): {
  state: AudioRecorderState;
  controls: AudioRecorderControls;
  browserSupport: BrowserSupport;
} {
  const {
    quality = 'high',
    maxDuration = 7200,
    autoUpload = false,
    enableLevelMonitoring = true,
    onStart,
    onStop,
    onPause,
    onResume,
    onUploadProgress,
    onUploadComplete,
    onError,
    onLevelChange,
  } = config;

  // Browser support detection
  const browserSupport = detectBrowserSupport();

  // State
  const [state, setState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingData, setRecordingData] = useState<RecordingData | null>(null);
  const recordingDataRef = useRef<RecordingData | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<{ type: AudioRecorderError; message: string } | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [errorDetails, setErrorDetails] = useState<AudioRecorderErrorDetails | null>(null);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<Date | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelMonitorIntervalRef = useRef<number | null>(null);
  const uploadClientRef = useRef<AudioUploadClient | null>(null);

  /**
   * Initialize upload client
   */
  useEffect(() => {
    const config = getAudioAPIConfig();
    
    console.log('[AudioRecorder] Initializing upload client with URL:', config.uploadApiUrl);
    
    uploadClientRef.current = new AudioUploadClient(
      config.uploadApiUrl,
      async () => {
        try {
          console.log('[AudioRecorder] Getting Firebase ID token...');
          const token = await getIdToken();
          if (!token) {
            console.error('[AudioRecorder] No authentication token available - user not signed in');
            throw new Error('No authentication token available. Please sign in to upload recordings.');
          }
          console.log('[AudioRecorder] Got Firebase token successfully');
          return token;
        } catch (error) {
          console.error('[AudioRecorder] Failed to get authentication token:', error);
          throw new Error('Authentication required. Please sign in to upload recordings.');
        }
      }
    );
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopLevelMonitoring();
      stopDurationTimer();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  /**
   * Handle errors with enhanced error details and recovery
   */
  const handleError = useCallback(async (type: AudioRecorderError, originalError?: any) => {
    // Parse the error to get more details
    const parsedError = originalError ? parseError(originalError) : { type, details: '' };
    
    // Create detailed error information
    const details = createErrorDetails(
      parsedError.type,
      parsedError.details || originalError?.message
    );
    
    // Log the error
    AudioRecorderErrorLogger.log(details);
    
    // Set error state
    setError({ type: details.type, message: details.message });
    setErrorDetails(details);
    setState('error');
    
    // Attempt automatic recovery if applicable
    if (AudioRecorderErrorRecovery.shouldAttemptRecovery(details, recoveryAttempts)) {
      console.log(`Attempting automatic recovery (attempt ${recoveryAttempts + 1})...`);
      
      const recovered = await AudioRecorderErrorRecovery.attemptRecovery(
        details,
        async () => {
          // Retry the last operation based on context
          if (type === 'device-error' || type === 'permission-denied') {
            await requestPermission();
          }
        }
      );
      
      if (recovered) {
        console.log('Automatic recovery successful');
        setRecoveryAttempts(0);
        setError(null);
        setErrorDetails(null);
        setState('idle');
        return;
      } else {
        setRecoveryAttempts(prev => prev + 1);
      }
    }
    
    // Call user's error callback
    if (onError) {
      onError(details.type, details.message);
    }
  }, [onError, recoveryAttempts]);

  /**
   * Request microphone permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // Release immediately
      setHasPermission(true);
      return true;
    } catch (err) {
      console.error('Permission denied:', err);
      setHasPermission(false);
      await handleError('permission-denied', err);
      return false;
    }
  }, [handleError]);

  /**
   * Start duration timer
   */
  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    durationIntervalRef.current = window.setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = (Date.now() - startTimeRef.current.getTime()) / 1000;
        setDuration(elapsed);

        // Check max duration
        if (elapsed >= maxDuration) {
          stopRecording();
        }
      }
    }, 100); // Update every 100ms for smooth timer
  }, [maxDuration]);

  /**
   * Stop duration timer
   */
  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  /**
   * Start audio level monitoring
   */
  const startLevelMonitoring = useCallback((stream: MediaStream) => {
    if (!enableLevelMonitoring) return;

    try {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      levelMonitorIntervalRef.current = window.setInterval(() => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
          const level = Math.min(100, (average / 255) * 100);
          setAudioLevel(level);
          if (onLevelChange) {
            onLevelChange(level);
          }
        }
      }, 100);
    } catch (err) {
      console.warn('Failed to start level monitoring:', err);
    }
  }, [enableLevelMonitoring, onLevelChange]);

  /**
   * Stop audio level monitoring
   */
  const stopLevelMonitoring = useCallback(() => {
    if (levelMonitorIntervalRef.current) {
      clearInterval(levelMonitorIntervalRef.current);
      levelMonitorIntervalRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  /**
   * Start recording
   */
  const startRecording = useCallback(async () => {
    if (!browserSupport.mediaRecorderSupported) {
      await handleError('not-supported');
      return;
    }

    if (!hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }

    try {
      // Get quality settings
      const qualitySettings = getQualitySettings(quality);

      // Request audio stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: qualitySettings.sampleRate,
          echoCancellation: qualitySettings.echoCancellation,
          noiseSuppression: qualitySettings.noiseSuppression,
          autoGainControl: qualitySettings.autoGainControl,
        },
      });

      mediaStreamRef.current = stream;

      // Create MediaRecorder
      const mimeType = browserSupport.preferredMimeType || 'audio/webm';
      const options = {
        mimeType,
        audioBitsPerSecond: qualitySettings.bitrate,
      };

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const endTime = new Date();
        const actualDuration = (endTime.getTime() - (startTimeRef.current?.getTime() || 0)) / 1000;

        const data: RecordingData = {
          blob,
          duration: actualDuration,
          format: getFormatFromMimeType(mimeType),
          mimeType,
          sampleRate: qualitySettings.sampleRate,
          startedAt: startTimeRef.current || new Date(),
          endedAt: endTime,
          size: blob.size,
        };

        setRecordingData(data);
        recordingDataRef.current = data;
        setState('stopped');
        stopLevelMonitoring();
        stopDurationTimer();

        if (onStop) {
          onStop(data);
        }

        // Auto-upload if enabled
        if (autoUpload) {
          uploadRecording(data);
        }
      };

      mediaRecorder.onerror = async (event: Event) => {
        await handleError('recording-error', event);
      };

      // Start recording
      startTimeRef.current = new Date();
      mediaRecorder.start(1000); // Collect data every second
      setState('recording');
      setDuration(0);
      setError(null);

      startDurationTimer();
      startLevelMonitoring(stream);

      if (onStart) {
        onStart();
      }
    } catch (err) {
      await handleError('device-error', err);
    }
  }, [
    browserSupport,
    hasPermission,
    quality,
    autoUpload,
    requestPermission,
    handleError,
    startDurationTimer,
    startLevelMonitoring,
    stopLevelMonitoring,
    stopDurationTimer,
    onStart,
    onStop,
  ]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  }, []);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setState('paused');
      stopDurationTimer();
      stopLevelMonitoring();
      if (onPause) {
        onPause();
      }
    }
  }, [stopDurationTimer, stopLevelMonitoring, onPause]);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');
      startDurationTimer();
      if (mediaStreamRef.current) {
        startLevelMonitoring(mediaStreamRef.current);
      }
      if (onResume) {
        onResume();
      }
    }
  }, [startDurationTimer, startLevelMonitoring, onResume]);

  /**
   * Cancel recording
   */
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
    audioChunksRef.current = [];
    setRecordingData(null);
    recordingDataRef.current = null;
    setState('idle');
    setDuration(0);
    stopDurationTimer();
    stopLevelMonitoring();
  }, [stopDurationTimer, stopLevelMonitoring]);

  /**
   * Upload recording
   */
  const uploadRecording = useCallback(async (data?: RecordingData): Promise<string | null> => {
    const activeRecording = data || recordingDataRef.current || recordingData;
    if (!activeRecording) {
      await handleError('upload-error', new Error('No recording data to upload'));
      return null;
    }

    if (!uploadClientRef.current) {
      await handleError('upload-error', new Error('Upload client not initialized'));
      return null;
    }

    try {
      console.log('[AudioRecorder] Starting upload...', {
        blobSize: activeRecording.blob.size,
        format: activeRecording.format,
        duration: activeRecording.duration,
      });
      
      setState('uploading');
      setUploadProgress(0);

      const result = await uploadClientRef.current.upload(
        activeRecording.blob,
        {
          platform: 'web',
          sourceType: 'web-recording',
          format: activeRecording.format as any,
          duration: activeRecording.duration,
          sampleRate: activeRecording.sampleRate,
          recordedAt: activeRecording.startedAt.toISOString(),
          deviceInfo: getWebDeviceInfo(),
        },
        {
          onProgress: (percent) => {
            console.log('[AudioRecorder] Upload progress:', percent + '%');
            setUploadProgress(percent);
            if (onUploadProgress) {
              onUploadProgress(percent);
            }
          },
        }
      );

      console.log('[AudioRecorder] Upload completed successfully:', result.audioId);
      setState('stopped');
      setUploadProgress(100);

      if (onUploadComplete) {
        onUploadComplete(result.audioId);
      }

      return result.audioId;
    } catch (err) {
      console.error('[AudioRecorder] Upload failed with error:', err);
      console.error('[AudioRecorder] Error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      await handleError('upload-error', err);
      setState('stopped');
      return null;
    }
  }, [recordingData, handleError, onUploadProgress, onUploadComplete]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    cancelRecording();
    setRecordingData(null);
    recordingDataRef.current = null;
    setError(null);
    setErrorDetails(null);
    setUploadProgress(0);
    setRecoveryAttempts(0);
  }, [cancelRecording]);

  // Return state and controls
  return {
    state: {
      state,
      duration,
      audioLevel,
      recordingData,
      uploadProgress,
      error,
      hasPermission,
      isSupported: browserSupport.mediaRecorderSupported,
      errorDetails, // Add detailed error info
    },
    controls: {
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      cancelRecording,
      requestPermission,
      uploadRecording,
      reset,
    },
    browserSupport,
  };
}

